import { useState, type ReactNode } from "react";
import { Box, Collapse } from "@mui/material";

export interface AmenityItem {
  key: string;
  name: string;
}
export interface AmenityCategory {
  name: string;
  amenities: AmenityItem[];
}

interface CollapsibleAmenitiesProps {
  categories: AmenityCategory[];
  /** True when the amenity key is currently selected. */
  isSelected: (key: string) => boolean;
  /** Toggle a single amenity on/off. */
  onToggle: (item: AmenityItem) => void;
  title?: ReactNode;
  subtitle?: ReactNode;
  hint?: ReactNode;
}

/** Palette from the approved design (amenities-collapsible.html). */
const C = {
  primary: "#5B2FF9",
  primarySoft: "#EEE9FF",
  ink: "#1B1235",
  muted: "#8A8699",
  line: "#E7E4F0",
  openLine: "#D8CEFF",
  chipLine: "#DCD8EA",
  chipText: "#4A4560",
};

const Chevron = ({ open }: { open: boolean }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.2}
    strokeLinecap="round"
    strokeLinejoin="round"
    sx={{
      width: 20,
      height: 20,
      flexShrink: 0,
      color: open ? C.primary : C.muted,
      transform: open ? "rotate(180deg)" : "none",
      transition: "transform .25s",
    }}
  >
    <polyline points="6 9 12 15 18 9" />
  </Box>
);

/**
 * Collapsible amenities picker (approved design: amenities-collapsible.html).
 * Each category is an accordion row with a selected-count badge + chevron; the
 * body holds selectable pill chips. Selection is caller-owned via
 * isSelected/onToggle, so it plugs into both Set<key> and [{key,name}] models.
 */
export function CollapsibleAmenities({
  categories,
  isSelected,
  onToggle,
  title,
  subtitle,
  hint,
}: CollapsibleAmenitiesProps) {
  // First category open by default (until the user interacts) — `null` lets the
  // default resolve after the categories load asynchronously.
  const [openSet, setOpenSet] = useState<Set<string> | null>(null);
  const effectiveOpen =
    openSet ?? new Set(categories[0] ? [categories[0].name] : []);

  const toggleOpen = (name: string) =>
    setOpenSet(() => {
      const next = new Set(effectiveOpen);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });

  return (
    <Box sx={{ fontFamily: "'Tajawal', sans-serif" }}>
      {title != null && (
        <Box sx={{ fontSize: 20, fontWeight: 800, color: C.ink }}>{title}</Box>
      )}
      {subtitle != null && (
        <Box sx={{ fontSize: 13, color: C.muted, mt: "4px", mb: "18px" }}>
          {subtitle}
        </Box>
      )}

      {categories.map((cat) => {
        const isOpen = effectiveOpen.has(cat.name);
        const count = cat.amenities.filter((a) => isSelected(a.key)).length;
        return (
          <Box
            key={cat.name}
            sx={{
              border: `1px solid ${isOpen ? C.openLine : C.line}`,
              borderRadius: "16px",
              mb: "12px",
              overflow: "hidden",
              background: "#fff",
              transition: "border-color .2s",
            }}
          >
            <Box
              onClick={() => toggleOpen(cat.name)}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                p: "16px 18px",
                cursor: "pointer",
                userSelect: "none",
                "&:hover": { background: "#FAF9FE" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <Box sx={{ fontSize: 16, fontWeight: 700, color: C.ink }}>
                  {cat.name}
                </Box>
                {count > 0 && (
                  <Box
                    sx={{
                      background: C.primary,
                      color: "#fff",
                      fontSize: 12,
                      fontWeight: 700,
                      minWidth: 22,
                      height: 22,
                      borderRadius: "11px",
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      px: "7px",
                    }}
                  >
                    {count}
                  </Box>
                )}
              </Box>
              <Chevron open={isOpen} />
            </Box>

            <Collapse in={isOpen} timeout="auto" unmountOnExit>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "10px",
                  p: "4px 18px 20px",
                }}
              >
                {cat.amenities.map((item) => {
                  const sel = isSelected(item.key);
                  return (
                    <Box
                      key={item.key}
                      component="button"
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onToggle(item);
                      }}
                      sx={{
                        border: `1.5px solid ${sel ? C.primary : C.chipLine}`,
                        background: sel ? C.primarySoft : "#fff",
                        color: sel ? C.primary : C.chipText,
                        borderRadius: "999px",
                        p: "9px 18px",
                        fontSize: 14,
                        fontWeight: sel ? 700 : 500,
                        fontFamily: "inherit",
                        cursor: "pointer",
                        transition: "all .15s",
                        whiteSpace: "nowrap",
                        "&:hover": { borderColor: sel ? C.primary : "#BCAEF5" },
                      }}
                    >
                      {item.name}
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {hint != null && (
        <Box
          sx={{ fontSize: 12, color: C.muted, textAlign: "center", mt: "14px" }}
        >
          {hint}
        </Box>
      )}
    </Box>
  );
}

export default CollapsibleAmenities;
